import { Slot } from "@radix-ui/react-slot";
import { PROJECTS_REGEX } from "files/hooks/useFiles";
import ProjectPrivilege from "rwb/pages/project/types/enums/privilege";
import useProjectsAccessList from "~/hooks/useProjectsAccessList";
import useUserPermission from "~/hooks/useUserPermission";
import useToast from "./toast";

const { VITE_RWB_API } = import.meta.env;

interface FileDownloadProps {
  fileKey?: string;
  children?: React.ReactNode;
}

const FileDownload = ({ fileKey = "", children }: FileDownloadProps) => {
  const { showError } = useToast();
  const downloadEnabled = useUserPermission("create:file-browser-download");
  const { data: projectAccessList } = useProjectsAccessList();

  const fileTitle = fileKey.split("/").at(-1) ?? "";
  const projectIdMatch = fileKey.match(PROJECTS_REGEX);

  if (!downloadEnabled) {
    return null;
  }

  if (projectIdMatch) {
    const projectId = projectIdMatch.groups?.projectId;
    const accessInfo = projectAccessList?.find(
      (project) => project.project_id === Number(projectId)
    );

    if (!accessInfo?.assigned_privileges.includes(ProjectPrivilege.DOWNLOAD_PROJECT_FILES)) {
      // Hide download CTA if it's a workbench file and current user doesn't have access to download
      return null;
    }
  }

  const downloadFile = async () => {
    const resp = await fetch(`${VITE_RWB_API}/file-browser/generate-download-url`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key: fileKey }),
    });

    if (resp.ok) {
      const presignedDownloadUrl = await resp.json();
      if (presignedDownloadUrl) {
        // Create temp link to trigger download programmatically
        const anchor = document.createElement("a");
        anchor.href = presignedDownloadUrl;
        anchor.style.display = "none";
        anchor.download = fileTitle;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      }
    } else {
      showError("Unable to download file");
    }
  };

  const onClick = async () => {
    await downloadFile();
  };

  return (
    <>
      <Slot onClick={onClick}>{children}</Slot>
    </>
  );
};

export { FileDownload, type FileDownloadProps };
